import {
  Box,
  Button,
  BoxProps,
  HStack,
  Flex,
  useColorMode,
  useTheme,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useState, useEffect } from 'react';
import ReactDatePicker, { CalendarContainer } from 'react-datepicker';

import { utils as commonUtils } from '@finley-technologies/common';

import './dateRangePicker.scss';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { popperStyling } from '~/packages/common-frontend/styles';
import {
  ButtonLikePattern,
  TypographyPattern,
} from '~/packages/common-frontend/theme/patterns';
import { TEXT_COLORS } from '~/packages/common-frontend/theme/tokens';
import {
  createResolveColor,
  useSimpleColorMode,
} from '~/packages/common-frontend/theme/utils';

// need a generator function that generates a map of key to label + start + end dates
export enum DatePickerPeriod {
  past7Days = '7Days',
  past30Days = '30Days',
  past90Days = '90Days',
  pastYear = 'Year',
}

const periodToDaysMap: Record<DatePickerPeriod, number> = {
  '7Days': 7,
  '30Days': 30,
  '90Days': 90,
  Year: 365,
};

const DatePickerPeriodButton = ({
  datePickerPeriod,
  onClick,
  currentSelectedDatePickerPeriod,
}: {
  datePickerPeriod: DatePickerPeriod;
  onClick: () => void;
  currentSelectedDatePickerPeriod: DatePickerPeriod | null;
}) => {
  const isCurrentlySelected =
    datePickerPeriod === currentSelectedDatePickerPeriod;

  return (
    <Button
      isActive={isCurrentlySelected}
      onClick={onClick}
      px="10px"
      size="sm"
      variant="secondary-muted"
    >
      {commonUtils.toTitleCase(datePickerPeriod)}
    </Button>
  );
};

type FullDatePickerCalendarContainerProps = {
  currentSelectedDatePickerPeriod: DatePickerPeriod | null;
  onSelectDefaultPeriod: (period: DatePickerPeriod | null) => void;
  showPreSelectedOptions?: boolean;
  children: React.ReactNode | React.ReactNode[];
};
const FullDatePickerCalendarContainer = ({
  currentSelectedDatePickerPeriod,
  onSelectDefaultPeriod,
  showPreSelectedOptions = true,
  children,
}: FullDatePickerCalendarContainerProps) => {
  const mode = useSimpleColorMode();
  const color = mode(colors.gray[900], 'white');
  const clearButtonColor = mode('inherit', colors.gray[900]);

  return (
    <CalendarContainer>
      {showPreSelectedOptions && (
        <HStack gap="0.5rem" pb="1rem">
          <Typography fontWeight="bold" variant="detail" TextProps={{ color }}>
            Past
          </Typography>
          {_.map(DatePickerPeriod, (period) => (
            <DatePickerPeriodButton
              datePickerPeriod={period}
              onClick={() => onSelectDefaultPeriod(period)}
              currentSelectedDatePickerPeriod={currentSelectedDatePickerPeriod}
              key={`datepicker-period-button-${period}`}
            />
          ))}
        </HStack>
      )}
      <Box position="relative">
        <Flex direction="column">
          {children}
          <Button
            bgColor="white"
            mt="0.5rem"
            onClick={() => onSelectDefaultPeriod(null)}
            size="sm"
            variant="secondary-muted"
          >
            <Typography
              fontWeight="medium"
              TextProps={{ color: clearButtonColor }}
              variant="detail"
            >
              Clear
            </Typography>
          </Button>
        </Flex>
      </Box>
    </CalendarContainer>
  );
};

/* ---- NOTES ----
A serious limitation of <FullDateRangePicker/> is that we cannot set {startDate = date, endDate = null}. Instead we use <FilterDatePicker/> to achieve this.
Issue stems from a limitation in ReactDatePicker package. Next steps would ideally be to use a different date picker package. */
export type DateRange = { startDate: Date | null; endDate: Date | null };
type FullDateRangePickerProps = {
  onSelectDateRange: (newDateRange: [Date | null, Date | null]) => void;
  onCalendarClose?: () => void;
  currentDateRange: DateRange;
  minDate?: Date;
  maxDate?: Date;
  showPreSelectedOptions?: boolean;
  isDisabled?: boolean;
  popperStyles?: React.CSSProperties;
  size?: 'sm' | 'md';
  placeholderText?: string;
  sx?: never;
} & BoxProps;
export const FullDateRangePicker = ({
  onSelectDateRange,
  onCalendarClose,
  currentDateRange,
  minDate,
  maxDate,
  showPreSelectedOptions = true,
  isDisabled = false,
  popperStyles = popperStyling,
  size = 'md',
  placeholderText = 'Select Date Range',
  sx: _externalSx,
  ...remainingBoxProps
}: FullDateRangePickerProps) => {
  // Also managing date range state down in this DatePicker so that selecting a start date
  // does not rerender the entire component and call the data refetch.
  // Refetch should only happen when start and end dates are both selected
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(currentDateRange);
  const [
    selectedDefaultPeriod,
    setSelectedDefaultPeriod,
  ] = useState<DatePickerPeriod | null>(null);

  // check to see if the currently selected dates are one of the default settings
  // even if user manually set the dates in the datepicker, we will be able to show that it is a default
  useEffect(() => {
    setDateRange(currentDateRange);

    if (currentDateRange.startDate && currentDateRange.endDate) {
      const currentDateDay = DateTime.now()
        .setZone('America/New_York')
        .startOf('day');
      const chartDataStartDateDay = DateTime.fromJSDate(
        currentDateRange.startDate,
      )
        .setZone('America/New_York')
        .startOf('day');
      const chartDataEndDateDay = DateTime.fromJSDate(currentDateRange.endDate)
        .setZone('America/New_York')
        .startOf('day');

      if (chartDataEndDateDay.toMillis() === currentDateDay.toMillis()) {
        const dateDiff = chartDataEndDateDay.diff(
          chartDataStartDateDay,
          'days',
        );
        const dateDiffDays = dateDiff.toObject().days;

        switch (dateDiffDays) {
          case 7:
            setSelectedDefaultPeriod(DatePickerPeriod.past7Days);
            break;
          case 30:
            setSelectedDefaultPeriod(DatePickerPeriod.past30Days);
            break;
          case 90:
            setSelectedDefaultPeriod(DatePickerPeriod.past90Days);
            break;
          case 365:
            setSelectedDefaultPeriod(DatePickerPeriod.pastYear);
            break;
          default:
            setSelectedDefaultPeriod(null);
        }
      }
    }
  }, [currentDateRange]);

  const onSelectDefaultPeriod = (period: DatePickerPeriod | null) => {
    if (!period) {
      setDateRange({ startDate: null, endDate: null });
      onSelectDateRange([null, null]);
      setSelectedDefaultPeriod(null);

      return;
    }
    const currentDate = new Date();

    const startDate = DateTime.fromJSDate(currentDate)
      .minus({ days: periodToDaysMap[period] })
      .toJSDate();
    setDateRange({ startDate, endDate: currentDate });
    onSelectDateRange([startDate, currentDate]);
    setSelectedDefaultPeriod(period);
  };

  const theme = useTheme();
  const { colorMode } = useColorMode();
  const resolveColor = createResolveColor({ colorMode });

  const popperBgColor = resolveColor({
    light: colors.gray[50],
    dark: colors.gray[900],
  });

  const { startDate, endDate } = dateRange;
  return (
    <Box
      className="fullDatePicker-main-container transparentBg"
      borderWidth="thin"
      onClick={(event) => {
        // React DatePicker's popper (popover/dialog) is opened by focusing the input. The input is
        // smaller than this box, which results in "dead areas" where clicking on the box will not
        // open the popper. This is a quick workaround that manually focuses the input when:
        // 1. the box is clicked
        // 2. popper is not already opened
        const inputElem = event.currentTarget.querySelector('input');
        const popperElem = event.currentTarget.querySelector(
          '.react-datepicker-popper',
        );
        if (inputElem instanceof HTMLInputElement && !popperElem) {
          inputElem.focus();
        }
      }}
      sx={{
        ...ButtonLikePattern.baseStyles.static,
        ...ButtonLikePattern.baseStyles.interactive,
        ...ButtonLikePattern.sizes.layout[size],
        ...ButtonLikePattern.sizes.fixedHeight[size],
        ...ButtonLikePattern.variants['outline-muted'].static({
          colorMode,
          colorScheme: 'gray',
          theme,
        }),
        ...ButtonLikePattern.variants['outline-muted'].interactive({
          colorMode,
          colorScheme: 'gray',
          theme,
        }),
        paddingBlock: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '& input': {
          ...TypographyPattern.variants['button-text'],
          padding: '0!important',
          outline: 0,
        },
        '& .react-datepicker__close-icon': {
          right: '-0.5rem',
        },
        '& .react-datepicker__close-icon::after': {
          alignItems: 'center',
          background: 'transparent',
          color: resolveColor(TEXT_COLORS.muted.base),
          display: 'flex',
          flexShrink: '0',
          fontSize: '1rem',
          height: '1rem',
          justifyContent: 'center',
          padding: '0',
          width: '1rem',
        },
      }}
      {...remainingBoxProps}
    >
      <Icon icon="calendar" style={{ marginRight: '0.5rem' }} />
      <ReactDatePicker
        showIcon={false}
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(newDateRange) => {
          const [newStartDate, newEndDate] = newDateRange;
          setDateRange({ startDate: newStartDate, endDate: newEndDate });
          onSelectDateRange(newDateRange);
        }}
        onCalendarClose={onCalendarClose}
        dateFormat="MMM d, yyyy"
        placeholderText={placeholderText}
        showPopperArrow={false}
        calendarContainer={({ children }) => (
          <FullDatePickerCalendarContainer
            currentSelectedDatePickerPeriod={selectedDefaultPeriod}
            onSelectDefaultPeriod={onSelectDefaultPeriod}
            showPreSelectedOptions={showPreSelectedOptions}
          >
            {children}
          </FullDatePickerCalendarContainer>
        )}
        maxDate={maxDate}
        minDate={minDate}
        disabled={isDisabled}
        dropdownMode="select"
        focusSelectedMonth={false}
        disabledKeyboardNavigation // bugfix: highlights one day of
        isClearable={!isDisabled}
        openToDate={startDate ?? undefined}
        showMonthDropdown
        showYearDropdown
        popperModifiers={[
          {
            name: 'setPopperStyles',
            enabled: true,
            phase: 'write',
            fn: ({ state }) => {
              const popperElement = state.elements.popper;
              if (popperElement) {
                if (state.placement.startsWith('top')) {
                  // Workaround: Fixing the popper's height to '28rem' when opened at the top to prevent navigation button moving month to month
                  popperElement.style.height = '28rem';
                } else {
                  popperElement.style.display = 'flex';
                }
                Object.assign(popperElement.style, popperStyles);
                popperElement.style.backgroundColor = popperBgColor;
                popperElement.style.zIndex = '3';
              }
            },
          },
        ]}
      />
    </Box>
  );
};
